import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, timer, of } from 'rxjs';
import { WidgetService } from 'app/shared/services/widget.service';
import { switchMap, map, take, filter, defaultIfEmpty, tap } from 'rxjs/operators';

export interface EditedWidgetInfo {
  widgetId: number;
  previewImageUrl: string;
}

@Injectable({ providedIn: 'root' })
export class WidgetPreviewService {
  private widgetService = inject(WidgetService);
  private readonly storageKey = 'editedWidgets';
  private readonly editedWidgetsSubject = new BehaviorSubject<EditedWidgetInfo[]>(this.readFromStorage());
  public readonly editedWidgets$: Observable<EditedWidgetInfo[]> = this.editedWidgetsSubject.asObservable();

  public addEditedWidget(widgetId: number, previewImageUrl: string): void {
    const current = this.editedWidgetsSubject.value ?? [];
    const index = current.findIndex((w) => w.widgetId === widgetId);
    let next: Array<{ widgetId: number; previewImageUrl: string }>;
    if (index === -1) {
      next = [...current, { widgetId, previewImageUrl }];
    } else {
      next = current.map((w) => (w.widgetId === widgetId ? { ...w, previewImageUrl } : w));
    }
    this.writeToStorage(next);
    this.editedWidgetsSubject.next(next);
  }

  public removeEditedWidget(widgetId: number): void {
    const next = this.editedWidgetsSubject.value.filter((w) => w.widgetId !== widgetId);
    this.writeToStorage(next);
    this.editedWidgetsSubject.next(next);
  }

  public clearEditedWidgets(): void {
    this.writeToStorage([]);
    this.editedWidgetsSubject.next([]);
  }

  private readFromStorage(): EditedWidgetInfo[] {
    const raw = sessionStorage.getItem(this.storageKey);
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  public checkForUpdatedPreviewImg$(widgetId: number): Observable<string | null> {
    const edited = this.editedWidgetsSubject.value.find((w) => w.widgetId === widgetId);
    if (!edited) return of(null);

    return timer(0, 3000).pipe(
      take(6),
      switchMap(() => this.widgetService.getWidgetById(widgetId)),
      map((widget) => widget?.states?.[0]?.previewImageUrl ?? null),
      filter((currentPreview) => currentPreview !== null && currentPreview !== edited.previewImageUrl),
      tap(() => this.removeEditedWidget(widgetId)),

      take(1),
      defaultIfEmpty(null),
    );
  }

  private writeToStorage(value: EditedWidgetInfo[]): void {
    sessionStorage.setItem(this.storageKey, JSON.stringify(value));
  }
}
